import { useState } from "react";
import { useMutation } from "@apollo/client/react";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { PERSONALIZE_SITUATION_SESSION } from "@/graphql/mutations/situations";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { PERSONALIZATION_PRIVACY_HINT } from "@/lib/personalization";

/**
 * „Pritaikyti" — rewords **a whole session** around the learner's world.
 *
 * A grown-up's action and never automatic: it is one real AI call per situation,
 * and a child opening a session should not wait for any of them. It belongs to
 * the session rather than to the learner because a rewrite is worth having for
 * the reading it is attached to — next month's session gets fresh stories rather
 * than one written about last year's obsession.
 *
 * The dialog says out loud what the rewrite will and will not do, because that is
 * the part a parent would otherwise have to discover: the moral question does not
 * move, and the child is never written into the story as a character — judging
 * somebody else is what makes these work.
 */
export function SituationPersonalizeDialog({
  sessionId,
  count,
  learnerName,
  personalized,
  theme,
}: {
  sessionId: string;
  count: number;
  learnerName?: string | null;
  personalized: boolean;
  theme?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState("");
  const [personalize, { loading }] = useMutation(PERSONALIZE_SITUATION_SESSION);

  const run = async () => {
    const text = profile.trim();
    if (!text) {
      toast.warning("Parašykite, ką AI turi žinoti apie mokinį");
      return;
    }
    try {
      const result: any = await personalize({
        variables: { sessionId, profile: text },
      });
      const payload = result?.data?.personalizeSituationSession;
      const errors = payload?.errors ?? [];
      if (errors.length > 0) {
        toast.error(errors[0]);
        return;
      }
      // Named one by one. „Nepavyko" for a set of three says nothing about which
      // story the grown-up is now looking at in the file's own words — and a
      // situation that kept its wording is an outcome, not a fault.
      (payload?.warnings ?? []).forEach((w: string) => toast.warning(w));
      toast.success("Sesija pritaikyta");
      setOpen(false);
    } catch (error) {
      toast.error("Nepavyko pritaikyti");
      console.error(error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={personalized ? "secondary" : "outline"}>
          <Sparkles className="mr-2 h-4 w-4" />
          {personalized ? "Pritaikyta" : "Pritaikyti"}
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Pritaikyti sesiją</DialogTitle>
          <DialogDescription>
            Visos {count} situacijos bus perrašytos apie tai, kas{" "}
            {learnerName || "mokiniui"} artima. Klausimai lieka tie patys ir klausia
            to paties.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Textarea
            value={profile}
            onChange={(e) => setProfile(e.target.value)}
            rows={4}
            placeholder="Ką AI turi žinoti? Pvz. „šoka hiphopą, turi jaunesnį brolį“"
          />
          <p className="text-sm text-muted-foreground">{PERSONALIZATION_PRIVACY_HINT}</p>

          {/* The rule worth stating: the child is not a character. Situations
              work because the reader judges somebody else. */}
          <p className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
            Vaiko vardas istorijose nenaudojamas — veikėjai lieka kiti žmonės.
            Taip vaikas sprendžia apie situaciją iš šalies, o ne gina save.
          </p>

          {/* Said before the button rather than discovered at the end of it. The
              stories are rewritten together, so a session takes about as long as
              its slowest one — measured at ~45s for three. */}
          <p className="text-sm text-muted-foreground">
            Kiekviena istorija perrašoma atskirai, todėl tai užtruks maždaug
            minutę. Jei kuri nors nepavyks, ji liks parašyta kaip faile — sesija
            veiks bet kuriuo atveju.
          </p>

          {personalized && theme && (
            <p className="text-sm text-muted-foreground">
              Dabar pritaikyta apie: <strong>{theme}</strong>. Paleidus dar kartą
              bus perrašyta iš naujo.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={loading}>
            Atšaukti
          </Button>
          <Button onClick={run} disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Pritaikyti
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
